<script setup lang="ts" generic="T, V = T, M extends boolean = false">
import { computed } from 'vue'

type ModelValue<V, M extends boolean> = M extends true ? V[] : V | null

interface SelectOption<T, V> {
  label: string
  value: V
  original: T
  disabled: boolean
}

const props = withDefaults(
  defineProps<{
    options?: T[]
    optionLabel?: string | ((option: T) => string)
    optionValue?: string
    optionDisabled?: string | ((option: T) => boolean)
    placeholder?: string
    filterPlaceholder?: string
    emptyMessage?: string
    emptyFilterMessage?: string
    disabled?: boolean
    loading?: boolean
    /**
     * Force the search input on or off. Left unset, the search appears only once the
     * list is long enough to be worth filtering (see FILTER_THRESHOLD).
     */
    filter?: boolean
    showClear?: boolean
    multiple?: M
    invalid?: boolean
    fluid?: boolean
    size?: 'small' | 'normal' | 'large' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  }>(),
  {
    options: () => [],
    optionLabel: undefined,
    optionValue: undefined,
    optionDisabled: undefined,
    placeholder: undefined,
    filterPlaceholder: undefined,
    emptyMessage: undefined,
    emptyFilterMessage: undefined,
    disabled: false,
    loading: false,
    filter: undefined,
    showClear: false,
    invalid: false,
    fluid: false,
    size: 'md',
  },
)

const emit = defineEmits<{
  change: [event: { value: ModelValue<V, M> }]
  filter: [event: { value: string; query: string }]
  'update:searchTerm': [value: string]
}>()

const model = defineModel<ModelValue<V, M>>({ required: true })
const searchTerm = defineModel<string>('searchTerm', { default: '' })

function readPath(value: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') return undefined
    return (current as Record<string, unknown>)[key]
  }, value)
}

function resolveOptionLabel(option: T): string {
  if (typeof props.optionLabel === 'function') return props.optionLabel(option)
  if (typeof props.optionLabel === 'string')
    return String(readPath(option, props.optionLabel) ?? '')
  if (option && typeof option === 'object' && 'label' in option) {
    return String((option as { label?: unknown }).label ?? '')
  }
  return option == null ? '' : String(option)
}

function resolveOptionValue(option: T): V {
  if (props.optionValue) return readPath(option, props.optionValue) as V
  return option as unknown as V
}

function resolveOptionDisabled(option: T): boolean {
  if (typeof props.optionDisabled === 'function') return props.optionDisabled(option)
  if (typeof props.optionDisabled === 'string')
    return Boolean(readPath(option, props.optionDisabled))
  return false
}

const normalizedOptions = computed<SelectOption<T, V>[]>(() =>
  props.options.map((option) => ({
    label: resolveOptionLabel(option),
    value: resolveOptionValue(option),
    original: option,
    disabled: resolveOptionDisabled(option),
  })),
)

/** Below this many options the list is short enough to scan, so a search box is only clutter. */
const FILTER_THRESHOLD = 10

// Call sites that load their options asynchronously pass `filter` explicitly, because their
// option count says nothing about how long the searchable list actually is.
const searchInput = computed(() => {
  const enabled = props.filter ?? normalizedOptions.value.length >= FILTER_THRESHOLD
  return enabled ? { placeholder: props.filterPlaceholder } : false
})

const uiSize = computed(() => {
  if (props.size === 'small') return 'sm'
  if (props.size === 'normal') return 'md'
  if (props.size === 'large') return 'lg'
  return props.size
})

function updateModel(value: ModelValue<V, M>) {
  model.value = value
  emit('change', { value })
}

function updateSearch(value: string) {
  searchTerm.value = value
  emit('update:searchTerm', value)
  emit('filter', { value, query: value })
}

defineExpose({
  focus: () => undefined,
})
</script>

<template>
  <USelectMenu
    :model-value="model as any"
    :items="normalizedOptions"
    label-key="label"
    value-key="value"
    :multiple
    :disabled
    :loading
    :placeholder
    :search-input="searchInput"
    :search-term="searchTerm"
    :clear="showClear"
    :color="invalid ? 'error' : 'primary'"
    :highlight="invalid"
    :size="uiSize"
    :class="{ 'w-full': fluid }"
    @update:model-value="updateModel($event as ModelValue<V, M>)"
    @update:search-term="updateSearch"
  >
    <template v-if="$slots.option" #item="{ item, index }">
      <slot name="option" :option="item.original" :index />
    </template>
    <template v-if="$slots.value" #default>
      <slot name="value" :value="model" :placeholder />
    </template>
    <template #empty>
      <slot name="empty">
        {{ searchTerm ? (emptyFilterMessage ?? emptyMessage) : emptyMessage }}
      </slot>
    </template>
    <template v-if="$slots.header" #content-top>
      <slot name="header" />
    </template>
    <template v-if="$slots.footer" #content-bottom>
      <slot name="footer" />
    </template>
  </USelectMenu>
</template>
